import {useEffect, useState} from "react";
import {Game, IWordScore} from "@game/game";
import {GameEvent} from "@game/gameEvent";
import {Tile} from "@game/map/tile";
import {Vector2} from "@utility/vector2";
import {TILE_SIZE, COLOURS} from "@components/core/theme";
import {CANDIDATE_OFFSETS, H, W, dotInSvg, labelLayout} from "@components/map/PlayerNameLabel";

type Period = "daily" | "weekly" | "monthly";

interface LeaderboardEntry extends IWordScore {
    period: Period;
    isPersonal: boolean;
}

function tileScore(nearbyTiles: Tile[], pos: Vector2): number {
    const tile = nearbyTiles.find(t => t.position.x === pos.x && t.position.y === pos.y);
    if (!tile) return -1;
    const hasObstruction = !!tile.place && ["block", "road", "decoration"].includes(tile.place.type);
    return hasObstruction ? 0 : tile.letter !== undefined ? 1 : 2;
}

function bestOffset(nearbyTiles: Tile[], pos: Vector2): Vector2 {
    let best = CANDIDATE_OFFSETS[0]!;
    let bestScore = -2;
    for (const offset of CANDIDATE_OFFSETS) {
        const score = tileScore(nearbyTiles, pos.plus(offset));
        if (score > bestScore) { bestScore = score; best = offset; }
        if (bestScore === 2) break;
    }
    return best;
}

function ScoreDot({cx, cy, period}: {cx: number; cy: number; period: Period}) {
    if (period === "daily") {
        return <circle cx={cx} cy={cy} r={5} fill="none" stroke={COLOURS.amber} strokeWidth={1.5}/>;
    }
    if (period === "weekly") {
        return <>
            <circle cx={cx} cy={cy} r={5} fill="none" stroke={COLOURS.amber} strokeWidth={1.5}/>
            <circle cx={cx} cy={cy} r={8} fill="none" stroke={COLOURS.amber} strokeWidth={1}/>
        </>;
    }
    // monthly: filled with outer ring
    return <>
        <circle cx={cx} cy={cy} r={5} fill={COLOURS.amber} stroke={COLOURS.parchment} strokeWidth={1}/>
        <circle cx={cx} cy={cy} r={8} fill="none" stroke={COLOURS.amber} strokeWidth={1}/>
    </>;
}

interface ILeaderboardLabels {
    game: Game;
    nearbyTiles: Tile[];
    onPersonalTiles?: (tiles: Set<string>) => void;
}

export function LeaderboardLabels({game, nearbyTiles, onPersonalTiles}: ILeaderboardLabels) {
    const [entries, setEntries] = useState<LeaderboardEntry[]>([]);

    useEffect(() => {
        const load = async () => {
            const myId = game.userPlayer?.playerId;
            const [global, personal] = await Promise.all([
                game.getUserGlobalLeaderboards(),
                game.getUserPlayerLeaderboards(),
            ]);

            // monthly > weekly > daily — higher rank wins when same tile appears in multiple periods
            const PERIOD_RANK: Record<string, number> = { daily: 0, weekly: 1, monthly: 2 };

            // Keyed by "regionId-tileId" — keep only the longest-term entry per tile
            const byTile = new Map<string, LeaderboardEntry>();

            const upsert = (entry: LeaderboardEntry) => {
                const k = `${entry.regionId}-${entry.tileId}`;
                const existing = byTile.get(k);
                if (!existing || PERIOD_RANK[entry.period]! > PERIOD_RANK[existing.period]!) {
                    byTile.set(k, entry);
                }
            };

            // Global entries — bold unless it belongs to the current user
            for (const [period, words] of Object.entries(global)) {
                for (const w of words) {
                    if (w.regionId !== undefined && w.tileId !== undefined) {
                        upsert({ ...w, period: period as Period, isPersonal: w.player === myId });
                    }
                }
            }

            // Personal entries — normal weight; same deduplication rule applies
            for (const [period, words] of Object.entries(personal)) {
                for (const w of words) {
                    if (w.regionId !== undefined && w.tileId !== undefined) {
                        upsert({ ...w, period: period as Period, isPersonal: true,
                            player: myId ?? "" });
                    }
                }
            }

            const resolved = Array.from(byTile.values());
            setEntries(resolved);
            onPersonalTiles?.(new Set(
                resolved.filter(e => e.isPersonal).map(e => {
                    const pos = game.size.getTilePosition({ regionId: e.regionId!, tileId: e.tileId! });
                    return `${pos.x},${pos.y}`;
                })
            ));
        };

        load().catch(console.error);

        const handleEvent = (event: GameEvent) => {
            if (event.type === "NewHighScore") load().catch(console.error);
        };
        game.events.delegates.add(handleEvent);
        return () => game.events.delegates.remove(handleEvent);
    }, [game]);

    const labels: JSX.Element[] = [];

    for (const entry of entries) {
        const tilePos = game.size.getTilePosition({ regionId: entry.regionId!, tileId: entry.tileId! });
        // Only render when the anchor tile is currently visible/loaded
        if (!nearbyTiles.some(t => t.position.x === tilePos.x && t.position.y === tilePos.y)) continue;

        const offset = bestOffset(nearbyTiles, tilePos);
        const dx = offset.x;
        const dy = offset.y;
        const px = tilePos.x * TILE_SIZE.x;
        const py = tilePos.y * TILE_SIZE.y;

        const { svgW, pathD, left, top } = labelLayout(dx, dy, px, py);
        const key = `lb-${entry.period}-${entry.regionId}-${entry.tileId}-${entry.player}`;
        const pathId = `lb-arc-${key}`;
        const dot = dotInSvg(dx, dy, svgW, left, top, px, py);
        const textOffset = dx === 0 && dy > 0 ? 12 : 0;

        const nickname = game.getNickname(entry.player) ?? "";
        const fontWeight = entry.isPersonal ? "normal" : "bold";

        labels.push(
            <svg key={key} width={svgW} height={H} overflow="visible"
                 style={{ position: "absolute", left, top, pointerEvents: "none", userSelect: "none" }}>
                <defs><path id={pathId} d={pathD}/></defs>
                {/* Parchment halo */}
                <text fontFamily="Georgia, 'Times New Roman', serif" fontSize="11.5"
                      letterSpacing="0.5" fill={COLOURS.parchment}
                      strokeWidth="3" stroke={COLOURS.parchment} strokeLinejoin="round" opacity="0.7">
                    <textPath href={`#${pathId}`} startOffset={textOffset || undefined}>
                        <tspan fontWeight={fontWeight}>{entry.word.toUpperCase()}</tspan>
                        <tspan fontStyle="italic" dx="4"> {nickname}</tspan>
                    </textPath>
                </text>
                {/* Main label */}
                <text fontFamily="Georgia, 'Times New Roman', serif" fontSize="11.5"
                      letterSpacing="0.5" fill={COLOURS.amber}>
                    <textPath href={`#${pathId}`} startOffset={textOffset || undefined}>
                        <tspan fontWeight={fontWeight}>{entry.word.toUpperCase()}</tspan>
                        <tspan fontStyle="italic" dx="4"> {nickname}</tspan>
                    </textPath>
                </text>
                <ScoreDot cx={dot.cx} cy={dot.cy} period={entry.period}/>
            </svg>
        );
    }

    return <>{labels}</>;
}
